import { normalizeEndpointPath } from "./probeUrlUtils";

const STORAGE_PREFIX = "validaite_gate0_";
export const GATE0_DRAFT_PROJECT_KEY = "validaite_gate0_draft_project_id";

export function loadDraftProjectId() {
  try {
    const raw = sessionStorage.getItem(GATE0_DRAFT_PROJECT_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

export function saveDraftProjectId(projectId) {
  if (projectId == null) return;
  try {
    sessionStorage.setItem(GATE0_DRAFT_PROJECT_KEY, String(projectId));
  } catch {
    /* ignore */
  }
}

export function clearDraftProjectId() {
  try {
    sessionStorage.removeItem(GATE0_DRAFT_PROJECT_KEY);
  } catch {
    /* ignore */
  }
}

/** Map Gate 0 criticality to portfolio risk_level chip value (uppercase). */
export function criticalityToRiskLevel(criticality) {
  if (!criticality) return null;
  return String(criticality).trim().toUpperCase();
}

/** Read criticality from a project row (gate0_data or confirmed intake_outcome). */
export function resolveProjectRiskLevel(project) {
  if (!project) return null;
  const fromGate0 = project.gate0_data?.criticality;
  const fromOutcome = project.intake_outcome?.gate0_data?.criticality;
  return criticalityToRiskLevel(fromGate0 || fromOutcome);
}

// Mirrors resolveEndpointOperation in apiSurfaceForm.js. Duplicated here to
// avoid a circular import (apiSurfaceForm already imports this module).
function resolveSavedEndpointOperation(ep = {}) {
  const hasOperation =
    Object.prototype.hasOwnProperty.call(ep, "operation") && ep.operation != null;
  const raw = String(
    hasOperation ? ep.operation : ep.purpose || ep.intent || ep.description || ""
  ).trim();
  if (!raw) return "";
  if (/^primary api endpoint$/i.test(raw) || /^endpoint \d+$/i.test(raw)) return "";
  if (/^from fr elicitation/i.test(raw)) return "";
  return raw;
}

/** @returns {Array<{ id: string, method: string, path: string, operation?: string, purpose?: string, request_body?: unknown, payloads?: object[] }>} */
export function normalizeEndpointsFromForm(form) {
  if (Array.isArray(form.endpoints) && form.endpoints.length > 0) {
    return form.endpoints
      .map((ep, i) => {
        const operation = resolveSavedEndpointOperation(ep);
        const row = {
          id: ep.id || `ep-${i + 1}`,
          method: String(ep.method || "GET").toUpperCase(),
          path: normalizeEndpointPath(ep.path),
          operation,
          purpose: operation || String(ep.purpose || "").trim() || `Endpoint ${i + 1}`,
        };
        if (ep.prompt_field) row.prompt_field = ep.prompt_field;
        if (ep.response_field) row.response_field = ep.response_field;
        if (Array.isArray(ep.payloads) && ep.payloads.length > 0) {
          row.payloads = ep.payloads
            .filter((p) => p && String(p.name || p.body || "").trim())
            .map((p, vi) => ({
              name: String(p.name || `variant-${vi + 1}`).trim(),
              body: p.body,
              expect_status:
                p.expect_status != null
                  ? p.expect_status
                  : p.expectStatus != null && String(p.expectStatus).trim()
                    ? parseInt(String(p.expectStatus).trim(), 10)
                    : null,
            }));
        } else if (ep.request_body != null) {
          row.request_body = ep.request_body;
        } else if (String(ep.requestBody || "").trim()) {
          try {
            row.request_body = JSON.parse(String(ep.requestBody).trim());
          } catch {
            row.request_body_raw = String(ep.requestBody).trim();
          }
        }
        return row;
      })
      .filter((ep) => ep.path.length > 0)
      .map((ep) => ({
        ...ep,
        path: ep.path.startsWith("/") ? ep.path : `/${ep.path}`,
      }));
  }

  const legacyPath = (form.endpointPath || form.endpoint_path || "").trim();
  if (!legacyPath) return [];
  return [
    {
      id: "ep-1",
      method: String(form.endpointMethod || form.endpoint_method || "GET").toUpperCase(),
      path: legacyPath.startsWith("/") ? legacyPath : `/${legacyPath}`,
      operation: (form.endpointOperation || form.endpoint_operation || form.endpointPurpose || form.endpoint_purpose || "").trim(),
      purpose: (form.endpointPurpose || form.endpoint_purpose || form.endpointOperation || form.endpoint_operation || "Primary API endpoint").trim(),
    },
  ];
}

export function buildGate0ApiPayload(form, extras = {}) {
  const frameworks = (form.frameworks || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const endpoints = normalizeEndpointsFromForm(form);

  const payload = {
    app_name: (form.appName || form.app_name || "").trim(),
    app_version: (form.appVersion || form.app_version || form.version || "").trim() || undefined,
    owner_name: (form.ownerName || form.owner_name || "").trim(),
    environment: form.environment || undefined,
    criticality: form.criticality || undefined,
    domain: form.domain || undefined,
    intended_use: (form.intendedUse || form.intended_use || "").trim() || undefined,
    frameworks: frameworks.length ? frameworks : undefined,
    base_url: (form.baseUrl || form.base_url || "").trim() || undefined,
    auth_type: (form.authType || form.auth_type || "").trim() || undefined,
    endpoints: endpoints.length ? endpoints : undefined,
  };

  if (extras.api_probe_summary) {
    payload.api_probe_summary = extras.api_probe_summary;
  }
  if (Array.isArray(extras.api_probe_history) && extras.api_probe_history.length > 0) {
    payload.api_probe_history = extras.api_probe_history.slice(-10);
  }

  return payload;
}

export function saveGate0(projectId, gate0) {
  if (!projectId || !gate0) return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(gate0));
  } catch {
    /* ignore quota */
  }
}

export function loadGate0(projectId) {
  if (!projectId) return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearGate0(projectId) {
  if (!projectId) return;
  try {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${projectId}`);
  } catch {
    /* ignore */
  }
}

/** Prefer navigation state, then sessionStorage. */
export function resolveGate0(projectId, locationState) {
  if (locationState?.gate0 && typeof locationState.gate0 === "object") {
    return locationState.gate0;
  }
  return loadGate0(projectId);
}

/** Strip credentials; keep probe metadata for risk assessment. */
export function isProbeSuccess(report) {
  if (!report || typeof report !== "object") return false;
  const status = String(report.overall_status || "").toLowerCase();
  return (
    status === "ready" ||
    status === "ready_with_warnings" ||
    status === "pass" ||
    status === "pass_with_warnings"
  );
}

export function sanitizeProbeReportForStorage(report) {
  if (!report) return null;
  return {
    overall_status: report.overall_status,
    probe_mode: report.probe_mode,
    generated_at: report.generated_at,
    endpoints: (report.endpoints || []).map((ep) => ({
      endpoint_id: ep.endpoint_id,
      payload_name: ep.payload_name,
      method: ep.method,
      path: ep.path,
      url: ep.url,
      status_code: ep.status_code,
      expect_status: ep.expect_status,
      latency_ms: ep.latency_ms,
      reachable: ep.reachable,
      auth_status: ep.auth_status,
      schema_status: ep.schema_status,
      blocking: ep.blocking,
      notes: ep.notes,
      connection: ep.connection ?? null,
      operation: ep.operation ?? null,
      response: ep.response ?? null,
      request_body_preview: ep.request_body_preview,
      response_body_preview: ep.response_body_preview,
      response_body_truncated: ep.response_body_truncated,
      response_body_hash: ep.response_body_hash,
      response_content_type: ep.response_content_type,
      response_from: ep.response_from,
      flags: ep.flags,
    })),
    flags_summary: report.flags_summary,
  };
}
